import Service, { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import type { DigitalAssetTraitExtension as DigitalAsset } from 'tio-data/data/traits/digital-asset.ext';
import type { PublicAsset } from 'tio-data/data/resources/public-asset.type';
import type { TasAssetExtension as TasAsset } from 'tio-data/data/resources/tas-asset.ext';
import type { PslfAsset } from 'tio-data/data/resources/pslf-asset.type';
import type { MatchAsset } from 'tio-data/data/resources/match-asset.type';
import type { ObservableAsset } from 'tio-data/data/resources/observable-asset.type';
import type { RawFileDigitalAsset } from 'tio-data/data/resources/raw-file-digital-asset.type';

interface DigitalAssetModels {
  '': DigitalAsset;
  digitalAsset: DigitalAsset;
  'digital-asset': DigitalAsset;
  publicAsset: PublicAsset;
  'public-asset': PublicAsset;
  tasAsset: TasAsset;
  'tas-asset': TasAsset;
  pslfAsset: PslfAsset;
  'pslf-asset': PslfAsset;
  matchAsset: MatchAsset;
  'match-asset': MatchAsset;
  observableAsset: ObservableAsset;
  'observable-asset': ObservableAsset;
  rawFileDigitalAsset: RawFileDigitalAsset;
  'raw-file-digital-asset': RawFileDigitalAsset;
}

export interface DigitalAssetDetailSignature {
  bucket: string;
  uuid: string;
  filename: string;
  name: string;
  key: string;
  classification?: string;
}

export default class DigitalAssetsService extends Service {
  @service declare store: Store;

  /**
   * Creates an unsaved asset from uploaded S3 object metadata.
   * @param modelName Asset resource name in camelCase or kebab-case; empty uses digitalAsset.
   * @param details Uploaded object metadata; key becomes fullPath and classification is preserved.
   * @returns The exact record created by the store, without saving it.
   * @throws If the store cannot create the requested asset record.
   */
  createAssetFromS3Details = <Name extends keyof DigitalAssetModels>(
    modelName: Name,
    details: DigitalAssetDetailSignature
  ) => {
    const assetType = modelName || 'digitalAsset';
    return this.store.createRecord(assetType, {
      bucket: details.bucket,
      uuid: details.uuid,
      filename: details.filename,
      name: details.name,
      fullPath: details.key,
      classification: details.classification,
    }) as DigitalAssetModels[Name];
  };
}
